/**
 * Structured error type for calendar conversion failures.
 *
 * Conversion functions return a discriminated `Result` rather than
 * throwing so the boundary layer (UI / projection / auditor) can render
 * a localised message instead of a raw exception string. The error
 * `code` is the stable, programmatic identifier; `message` is a
 * developer-facing English summary suitable for logs but not for UI.
 */
export type CalendarErrorCode =
  | 'unknown_era'
  | 'before_first_era'
  | 'day_before_era_start'
  | 'invalid_month'
  | 'invalid_day_of_month'
  | 'invalid_year_zero'
  | 'malformed_calendar';

export interface CalendarError {
  code: CalendarErrorCode;
  message: string;
  /** Optional structured context for the boundary layer to format. */
  context?: Record<string, string | number>;
}

/** Result returned by every conversion function. */
export type CalendarResult<T> = { ok: true; value: T } | { ok: false; error: CalendarError };

export function ok<T>(value: T): CalendarResult<T> {
  return { ok: true, value };
}

export function err<T = never>(
  code: CalendarErrorCode,
  message: string,
  context?: Record<string, string | number>,
): CalendarResult<T> {
  return { ok: false, error: context ? { code, message, context } : { code, message } };
}
